import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Building2, Plus, Upload, Trash2, Edit2, X, ImagePlus, Loader2, MapPin, Crosshair, Box,
} from 'lucide-react';
import { toast } from 'sonner';
import MassingView from './MassingView';
import LeadsPanel from './LeadsPanel';
import FinancialsPanel from './FinancialsPanel';
import PublicListingTab from './PublicListingTab';

const FLOOR_LABELS = { G: 'Ground / Commercial', A: '1st Floor', B: '2nd Floor', C: '3rd Floor' };
const FLOOR_KEYS = ['G', 'A', 'B', 'C'];
const SECTION_LABELS = {
  left: 'Left Building',
  center: 'Center Core',
  right: 'Right Building',
  far_right: 'Far Right Wing',
};
const TYPE_LABELS = {
  studio: 'Studio', '1_bed': '1 Bed', '2_bed': '2 Bed', '3_bed': '3 Bed',
  commercial: 'Commercial', office: 'Office', retail: 'Retail',
};
const COMMERCIAL_TYPES = new Set(['commercial', 'office', 'retail']);
const STATUS_LABELS = {
  for_sale: 'For Sale',
  rental: 'Rental',
  subsidized: 'Subsidized',
  do_not_sell: 'Do Not Sell',
  rental_or_sale: 'Rental or Sale',
  reserved: 'Reserved',
  sold: 'Sold',
};
const STATUS_COLORS = {
  for_sale: 'bg-green-50 text-green-700 border-green-200',
  rental: 'bg-blue-50 text-blue-700 border-blue-200',
  subsidized: 'bg-purple-50 text-purple-700 border-purple-200',
  do_not_sell: 'bg-gray-100 text-gray-600 border-gray-300',
  rental_or_sale: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  reserved: 'bg-orange-50 text-orange-700 border-orange-200',
  sold: 'bg-red-50 text-red-700 border-red-200',
};
// Hex colors for hotspot fill when "Color by status" is on.
const STATUS_HOTSPOT = {
  for_sale: '#22c55e',
  rental: '#3b82f6',
  subsidized: '#a855f7',
  do_not_sell: '#9ca3af',
  rental_or_sale: '#eab308',
  reserved: '#f97316',
  sold: '#ef4444',
};

const PLAN_KEYS = {
  rendering: { label: 'Street Rendering', field: 'rendering_b64' },
  G: { label: 'Ground / Commercial', field: 'plan_g_b64' },
  A: { label: '1st Floor Plan', field: 'plan_a_b64' },
  B: { label: '2nd Floor Plan', field: 'plan_b_b64' },
  C: { label: '3rd Floor Plan', field: 'plan_c_b64' },
};

const fmtPrice = (p) => (p == null || p === '' ? '—' : `$${Number(p).toLocaleString()}`);
const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

export default function PropertiesPanel({ api }) {
  const [properties, setProperties] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [projections, setProjections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planTab, setPlanTab] = useState('rendering');
  const [colorByStatus, setColorByStatus] = useState(false);

  // Unit dialog
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({});
  const [unitSaving, setUnitSaving] = useState(false);

  // Property dialog (create)
  const [propDialogOpen, setPropDialogOpen] = useState(false);
  const [propForm, setPropForm] = useState({ name: '', description: '' });

  // Hotspot interaction
  const [placingFor, setPlacingFor] = useState(null);  // unit currently being placed
  const [hoveredUnitId, setHoveredUnitId] = useState(null);
  const unitRowRefs = useRef({});

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/properties');
      setProperties(data);
      if (!activeId && data.length) setActiveId(data[0].id);
    } catch (e) { toast.error('Failed to load properties'); }
    setLoading(false);
  }, [api, activeId]);

  const loadActive = useCallback(async () => {
    if (!activeId) { setProperty(null); setUnits([]); setProjections(null); return; }
    try {
      const [pRes, uRes, projRes] = await Promise.all([
        api.get(`/properties/${activeId}`),
        api.get(`/properties/${activeId}/units`),
        api.get(`/properties/${activeId}/projections`),
      ]);
      setProperty(pRes.data);
      setUnits(uRes.data);
      setProjections(projRes.data);
    } catch (e) { toast.error('Failed to load property'); }
  }, [api, activeId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadActive(); }, [loadActive]);

  const createProperty = async () => {
    if (!propForm.name.trim()) { toast.error('Name required'); return; }
    try {
      const { data } = await api.post('/properties', propForm);
      toast.success('Property created');
      setPropDialogOpen(false);
      setPropForm({ name: '', description: '' });
      setActiveId(data.id);
      loadList();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteProperty = async () => {
    if (!property) return;
    if (!window.confirm(`Delete "${property.name}" and all its units?`)) return;
    try {
      await api.delete(`/properties/${property.id}`);
      toast.success('Property deleted');
      setActiveId('');
      loadList();
    } catch (e) { toast.error('Failed'); }
  };

  // Image upload — read file as data URL, PUT to property
  const handleImageUpload = async (e, planKey) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8 MB)'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      try {
        await api.put(`/properties/${property.id}`, { [PLAN_KEYS[planKey].field]: dataUrl });
        toast.success(`${PLAN_KEYS[planKey].label} uploaded`);
        loadActive();
      } catch (err) { toast.error('Upload failed'); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeImage = async (planKey) => {
    if (!window.confirm(`Remove ${PLAN_KEYS[planKey].label}?`)) return;
    try {
      await api.put(`/properties/${property.id}`, { [PLAN_KEYS[planKey].field]: '' });
      toast.success('Removed');
      loadActive();
    } catch (e) { toast.error('Failed'); }
  };

  // ----- Unit dialog -----
  const openCreateUnit = (defaults = {}) => {
    setEditingUnit(null);
    setUnitForm({
      unit_code: '',
      floor: defaults.floor || (planTab !== 'rendering' ? planTab : 'A'),
      section: 'left',
      type: '1_bed',
      price: '',
      monthly_rent: '',
      status: 'for_sale',
      notes: '',
    });
    setUnitDialogOpen(true);
  };

  const openEditUnit = (u) => {
    setEditingUnit(u);
    setUnitForm({
      unit_code: u.unit_code || '',
      floor: u.floor || 'A',
      section: u.section || 'left',
      type: u.type || '1_bed',
      price: u.price ?? '',
      monthly_rent: u.monthly_rent ?? '',
      status: u.status || 'for_sale',
      notes: u.notes || '',
    });
    setUnitDialogOpen(true);
  };

  const saveUnit = async () => {
    if (!unitForm.unit_code.trim()) { toast.error('Unit code required (e.g. A-101)'); return; }
    setUnitSaving(true);
    const payload = {
      ...unitForm,
      property_id: property.id,
      price: unitForm.price === '' ? null : Number(unitForm.price),
      monthly_rent: unitForm.monthly_rent === '' ? null : Number(unitForm.monthly_rent),
    };
    try {
      if (editingUnit) {
        await api.put(`/properties/${property.id}/units/${editingUnit.id}`, payload);
        toast.success('Unit updated');
      } else {
        await api.post(`/properties/${property.id}/units`, payload);
        toast.success('Unit added');
      }
      setUnitDialogOpen(false);
      loadActive();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setUnitSaving(false);
  };

  const deleteUnit = async (u) => {
    if (!window.confirm(`Delete unit ${u.unit_code}?`)) return;
    try {
      await api.delete(`/properties/${property.id}/units/${u.id}`);
      toast.success('Unit deleted');
      loadActive();
    } catch (e) { toast.error('Failed'); }
  };

  // ----- Hotspots -----
  const startPlacing = (u) => {
    // Auto-jump to the right plan tab so the user can immediately draw.
    const targetPlan = u.floor === 'A' || u.floor === 'B' || u.floor === 'C' ? u.floor : 'rendering';
    setPlanTab(targetPlan);
    setPlacingFor(u);
    toast(`Click + drag on the floor plan to place ${u.unit_code}. Press Esc to cancel.`);
  };

  const cancelPlacing = useCallback(() => setPlacingFor(null), []);

  // Esc cancels placement mode
  useEffect(() => {
    if (!placingFor) return;
    const onKey = (e) => { if (e.key === 'Escape') cancelPlacing(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [placingFor, cancelPlacing]);

  const saveHotspot = async (unitId, rect) => {
    try {
      await api.put(`/properties/${property.id}/units/${unitId}/hotspot`, {
        plan: planTab, ...rect,
      });
      toast.success('Hotspot placed');
      setPlacingFor(null);
      loadActive();
    } catch (e) { toast.error('Failed to save hotspot'); }
  };

  const clearHotspot = async (u) => {
    try {
      await api.put(`/properties/${property.id}/units/${u.id}/hotspot`, {});
      toast.success('Hotspot cleared');
      loadActive();
    } catch (e) { toast.error('Failed'); }
  };

  // Hotspots visible on the active plan tab
  const hotspotsForPlan = useMemo(
    () => units.filter(u => u.hotspot && u.hotspot.plan === planTab),
    [units, planTab]
  );

  // When a hotspot is hovered, scroll matching unit row into view + highlight
  useEffect(() => {
    if (!hoveredUnitId) return;
    const row = unitRowRefs.current[hoveredUnitId];
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [hoveredUnitId]);

  // Group units by floor for display
  const unitsByFloor = useMemo(() => {
    const out = { G: [], A: [], B: [], C: [] };
    for (const u of units) (out[u.floor] || (out[u.floor] = [])).push(u);
    return out;
  }, [units]);

  // Stats per floor
  const floorStats = useMemo(() => {
    const stats = {};
    for (const f of FLOOR_KEYS) {
      const list = unitsByFloor[f] || [];
      stats[f] = {
        count: list.length,
        revenue: list.reduce((s, u) => s + (Number(u.price) || 0), 0),
      };
    }
    return stats;
  }, [unitsByFloor]);

  if (loading) return <div className="p-8 text-center text-[#8E85A0]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5" data-testid="properties-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5" style={{ color: 'var(--accent)' }} />
          <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Properties</h2>
          {properties.length > 0 && (
            <select
              value={activeId} onChange={e => setActiveId(e.target.value)}
              className="ml-2 bg-transparent border border-white/10 rounded px-2 py-1 text-sm"
              style={{ color: 'var(--text)' }}
              data-testid="property-selector"
            >
              {properties.map(p => <option key={p.id} value={p.id} style={{ color: '#000' }}>{p.name}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPropDialogOpen(true)} className="ceo-pill flex items-center gap-1" data-testid="new-property-btn">
            <Plus className="h-3.5 w-3.5" /> New Property
          </button>
          {property && (
            <button onClick={deleteProperty} className="ceo-pill flex items-center gap-1 text-red-400" data-testid="delete-property-btn">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>
      </div>

      {!property ? (
        <div className="ceo-card-elevated p-10 text-center">
          <Building2 className="h-10 w-10 mx-auto mb-3 opacity-50" style={{ color: 'var(--accent)' }} />
          <p className="text-sm mb-4" style={{ color: 'var(--text)' }}>No property yet. Create one to start mapping floors and units.</p>
          <button onClick={() => setPropDialogOpen(true)} className="ceo-pill flex items-center gap-1 mx-auto">
            <Plus className="h-3.5 w-3.5" /> Create Property
          </button>
        </div>
      ) : (
        <>
          {/* Property summary */}
          <div className="ceo-card-elevated p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-xl font-bold" style={{ color: 'var(--text)' }}>{property.name}</h3>
                {property.description && <p className="text-sm mt-1 opacity-80" style={{ color: 'var(--text)' }}>{property.description}</p>}
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                {FLOOR_KEYS.map(f => (
                  <div key={f} className="text-center">
                    <p className="opacity-60" style={{ color: 'var(--text)' }}>{FLOOR_LABELS[f]}</p>
                    <p className="text-base font-bold" style={{ color: 'var(--accent)' }}>{floorStats[f].count} units</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Projections dashboard */}
          {projections && <ProjectionsDashboard data={projections} />}

          {/* Public Listing controls — toggle on/off, edit marketing copy, preview */}
          <PublicListingTab api={api} property={property} onSaved={loadActive} />

          {/* Financials — variable opex + mortgage calc + net cash flow */}
          <FinancialsPanel api={api} propertyId={property.id} />

          {/* Leads / CRM pipeline */}
          <LeadsPanel
            api={api}
            propertyId={property.id}
            units={units}
            onOpenUnit={openEditUnit}
          />

          {/* Floor plan tabs */}
          <div className="ceo-card-elevated p-5">
            <div className="flex items-center gap-1 mb-3 overflow-x-auto" data-testid="plan-tabs">
              {Object.entries(PLAN_KEYS).map(([k, v]) => (
                <button key={k} onClick={() => setPlanTab(k)}
                  className={`ceo-tab-btn ${planTab === k ? 'active' : ''}`}
                  data-testid={`plan-tab-${k}`}>
                  {v.label}
                </button>
              ))}
              <button onClick={() => setPlanTab('massing')}
                className={`ceo-tab-btn flex items-center gap-1 ${planTab === 'massing' ? 'active' : ''}`}
                data-testid="plan-tab-massing">
                <Box className="h-3 w-3" /> 3D Massing
              </button>
              <div className="flex-1" />
              {planTab !== 'massing' && (
                <label className="ceo-pill flex items-center gap-1.5 cursor-pointer text-xs" title="Color hotspots by their unit status">
                  <input type="checkbox" checked={colorByStatus} onChange={e => setColorByStatus(e.target.checked)}
                    className="accent-[var(--accent)]" data-testid="color-by-status-toggle" />
                  Color by status
                </label>
              )}
            </div>
            {planTab === 'massing' ? (
              <MassingView units={units} onSelectUnit={openEditUnit} />
            ) : (
              <PlanViewer
                planKey={planTab}
                src={property[PLAN_KEYS[planTab].field]}
                onUpload={(e) => handleImageUpload(e, planTab)}
                onRemove={() => removeImage(planTab)}
                hotspots={hotspotsForPlan}
                hoveredUnitId={hoveredUnitId}
                setHoveredUnitId={setHoveredUnitId}
                placingFor={placingFor}
                cancelPlacing={cancelPlacing}
                onSaveHotspot={saveHotspot}
                onHotspotClick={openEditUnit}
                colorByStatus={colorByStatus}
              />
            )}
          </div>

          {/* Units list */}
          <div className="ceo-card-elevated p-5">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>Unit Catalog</h3>
              <button onClick={() => openCreateUnit()} className="ceo-pill flex items-center gap-1" data-testid="add-unit-btn">
                <Plus className="h-3.5 w-3.5" /> Add Unit
              </button>
            </div>
            {units.length === 0 ? (
              <p className="text-sm text-center py-6 opacity-60" style={{ color: 'var(--text)' }}>No units yet. Click "Add Unit" to start mapping.</p>
            ) : (
              FLOOR_KEYS.map(f => unitsByFloor[f].length > 0 && (
                <div key={f} className="mb-5" data-testid={`floor-section-${f}`}>
                  <h4 className="text-xs font-bold uppercase tracking-wider mb-2 opacity-70" style={{ color: 'var(--text)' }}>{FLOOR_LABELS[f]} <span className="opacity-50 font-normal">· {unitsByFloor[f].length} units</span></h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid={`units-table-${f}`}>
                      <thead>
                        <tr className="text-left opacity-60" style={{ color: 'var(--text)' }}>
                          <th className="pb-1 pr-2">Unit</th>
                          <th className="pb-1 pr-2">Type</th>
                          <th className="pb-1 pr-2">Section</th>
                          <th className="pb-1 pr-2">Status</th>
                          <th className="pb-1 pr-2">Sale</th>
                          <th className="pb-1 pr-2">Rent/mo</th>
                          <th className="pb-1 pr-2">Notes</th>
                          <th className="pb-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {unitsByFloor[f].map(u => (
                          <tr key={u.id}
                            ref={el => { if (el) unitRowRefs.current[u.id] = el; }}
                            onMouseEnter={() => setHoveredUnitId(u.id)}
                            onMouseLeave={() => setHoveredUnitId(prev => prev === u.id ? null : prev)}
                            className={`border-t border-white/5 transition-colors ${hoveredUnitId === u.id ? 'bg-white/10 ring-1 ring-[var(--accent)]/40' : 'hover:bg-white/5'}`}
                            data-testid={`unit-row-${u.id}`}>
                            <td className="py-1.5 pr-2 font-mono font-bold" style={{ color: 'var(--accent)' }}>{u.unit_code}</td>
                            <td className="py-1.5 pr-2" style={{ color: 'var(--text)' }}>{TYPE_LABELS[u.type] || u.type}</td>
                            <td className="py-1.5 pr-2 opacity-80" style={{ color: 'var(--text)' }}>{SECTION_LABELS[u.section] || '—'}</td>
                            <td className="py-1.5 pr-2">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLORS[u.status] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABELS[u.status] || u.status}</span>
                            </td>
                            <td className="py-1.5 pr-2 font-mono" style={{ color: 'var(--text)' }}>{fmtPrice(u.price)}</td>
                            <td className="py-1.5 pr-2 font-mono" style={{ color: 'var(--text)' }}>{u.monthly_rent ? `$${Number(u.monthly_rent).toLocaleString()}` : '—'}</td>
                            <td className="py-1.5 pr-2 max-w-[260px] truncate opacity-80" style={{ color: 'var(--text)' }} title={u.notes || ''}>{u.notes || '—'}</td>
                            <td className="py-1.5 text-right whitespace-nowrap">
                              {u.hotspot ? (
                                <button onClick={() => clearHotspot(u)} title="Clear hotspot" className="opacity-70 hover:opacity-100 px-1" data-testid={`clear-hotspot-${u.id}`}><Crosshair className="h-3 w-3 text-green-400" /></button>
                              ) : (
                                <button onClick={() => startPlacing(u)} title="Place on floor plan" className="opacity-50 hover:opacity-100 px-1" data-testid={`place-hotspot-${u.id}`}><Crosshair className="h-3 w-3" style={{ color: 'var(--text)' }} /></button>
                              )}
                              <button onClick={() => openEditUnit(u)} className="opacity-70 hover:opacity-100 px-1" data-testid={`edit-unit-${u.id}`}><Edit2 className="h-3 w-3" style={{ color: 'var(--text)' }} /></button>
                              <button onClick={() => deleteUnit(u)} className="opacity-70 hover:opacity-100 px-1" data-testid={`delete-unit-${u.id}`}><Trash2 className="h-3 w-3 text-red-400" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Create property dialog */}
      {propDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPropDialogOpen(false)}>
          <div className="ceo-card-elevated p-5 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--text)' }}>New Property</h3>
            <div className="space-y-3">
              <input type="text" placeholder="Name (e.g. Adam's Way)" value={propForm.name}
                onChange={e => setPropForm(p => ({ ...p, name: e.target.value }))}
                className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}
                data-testid="property-name-input" />
              <textarea placeholder="Description (optional)" value={propForm.description}
                onChange={e => setPropForm(p => ({ ...p, description: e.target.value }))}
                className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm min-h-[60px]" style={{ color: 'var(--text)' }}
                data-testid="property-desc-input" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPropDialogOpen(false)} className="ceo-pill">Cancel</button>
              <button onClick={createProperty} className="ceo-pill" style={{ background: 'var(--accent)', color: '#000' }} data-testid="property-save-btn">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Unit dialog */}
      {unitDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setUnitDialogOpen(false)}>
          <div className="ceo-card-elevated p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>{editingUnit ? 'Edit Unit' : 'Add Unit'}</h3>
              <button onClick={() => setUnitDialogOpen(false)} className="opacity-70 hover:opacity-100"><X className="h-4 w-4" style={{ color: 'var(--text)' }} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Unit Code *">
                <input type="text" value={unitForm.unit_code} onChange={e => setUnitForm(p => ({ ...p, unit_code: e.target.value.toUpperCase() }))}
                  placeholder="A-101" className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm font-mono" style={{ color: 'var(--text)' }} data-testid="unit-code-input" />
              </Field>
              <Field label="Floor *">
                <select value={unitForm.floor} onChange={e => setUnitForm(p => ({ ...p, floor: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }} data-testid="unit-floor-input">
                  {FLOOR_KEYS.map(f => <option key={f} value={f} style={{ color: '#000' }}>{f} — {FLOOR_LABELS[f]}</option>)}
                </select>
              </Field>
              <Field label="Section">
                <select value={unitForm.section} onChange={e => setUnitForm(p => ({ ...p, section: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}>
                  {Object.entries(SECTION_LABELS).map(([k, v]) => <option key={k} value={k} style={{ color: '#000' }}>{v}</option>)}
                </select>
              </Field>
              <Field label="Type *">
                <select value={unitForm.type} onChange={e => setUnitForm(p => ({ ...p, type: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k} style={{ color: '#000' }}>{v}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={unitForm.status} onChange={e => setUnitForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k} style={{ color: '#000' }}>{v}</option>)}
                </select>
              </Field>
              <Field label="Price (Sale, USD)">
                <input type="number" step="1000" value={unitForm.price} onChange={e => setUnitForm(p => ({ ...p, price: e.target.value }))}
                  placeholder="e.g. 595000" className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm font-mono" style={{ color: 'var(--text)' }} />
              </Field>
              <Field label="Monthly Rent (USD)">
                <input type="number" step="50" value={unitForm.monthly_rent} onChange={e => setUnitForm(p => ({ ...p, monthly_rent: e.target.value }))}
                  placeholder="e.g. 1850" className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm font-mono" style={{ color: 'var(--text)' }}
                  data-testid="unit-rent-input" />
              </Field>
              <div className="col-span-2">
                <Field label="Notes">
                  <textarea value={unitForm.notes} onChange={e => setUnitForm(p => ({ ...p, notes: e.target.value }))}
                    placeholder="e.g. Best unit in building (corner, top floor)" className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm min-h-[60px]" style={{ color: 'var(--text)' }} />
                </Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setUnitDialogOpen(false)} className="ceo-pill">Cancel</button>
              <button onClick={saveUnit} disabled={unitSaving} className="ceo-pill" style={{ background: 'var(--accent)', color: '#000' }} data-testid="unit-save-btn">
                {unitSaving ? 'Saving…' : editingUnit ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider opacity-70 mb-1" style={{ color: 'var(--text)' }}>{label}</label>
      {children}
    </div>
  );
}

function PlanViewer({ planKey, src, onUpload, onRemove, hotspots = [], hoveredUnitId, setHoveredUnitId, placingFor, cancelPlacing, onSaveHotspot, onHotspotClick, colorByStatus }) {
  const containerRef = useRef(null);
  const [drag, setDrag] = useState(null);  // {startX, startY, x, y, w, h} during draw

  if (!src) {
    return (
      <label className="block border-2 border-dashed border-white/10 rounded-lg py-12 text-center cursor-pointer hover:border-white/30 transition-colors" data-testid={`upload-image-${planKey}`}>
        <ImagePlus className="h-8 w-8 mx-auto mb-2 opacity-50" style={{ color: 'var(--text)' }} />
        <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Upload {PLAN_KEYS[planKey].label}</p>
        <p className="text-xs opacity-60 mt-1" style={{ color: 'var(--text)' }}>PNG, JPG up to 8 MB</p>
        <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
      </label>
    );
  }

  const isPlacing = !!placingFor;

  // Convert pageX/Y to %-coords inside the container
  const toPercent = (clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
  };

  const onMouseDown = (e) => {
    if (!isPlacing) return;
    e.preventDefault();
    const p = toPercent(e.clientX, e.clientY);
    setDrag({ startX: p.x, startY: p.y, x: p.x, y: p.y, w: 0, h: 0 });
  };
  const onMouseMove = (e) => {
    if (!drag) return;
    const p = toPercent(e.clientX, e.clientY);
    const x = Math.min(drag.startX, p.x);
    const y = Math.min(drag.startY, p.y);
    const w = Math.abs(p.x - drag.startX);
    const h = Math.abs(p.y - drag.startY);
    setDrag({ ...drag, x, y, w, h });
  };
  const onMouseUp = () => {
    if (!drag || !placingFor) { setDrag(null); return; }
    if (drag.w < 1 || drag.h < 1) {
      // Treat tiny drags as a cancel (likely a click, not a drag).
      setDrag(null);
      return;
    }
    onSaveHotspot(placingFor.id, { x: drag.x, y: drag.y, w: drag.w, h: drag.h });
    setDrag(null);
  };

  return (
    <div className="relative">
      {isPlacing && (
        <div className="mb-2 flex items-center justify-between gap-2 px-3 py-2 rounded bg-[var(--accent)]/10 border border-[var(--accent)]/30 text-xs" data-testid="placement-banner">
          <span style={{ color: 'var(--text)' }}>
            <Crosshair className="inline h-3.5 w-3.5 mr-1" /> Click and drag on the plan to place <strong style={{ color: 'var(--accent)' }}>{placingFor.unit_code}</strong>. Press Esc to cancel.
          </span>
          <button onClick={cancelPlacing} className="ceo-pill text-xs">Cancel</button>
        </div>
      )}
      <div
        ref={containerRef}
        className={`relative inline-block w-full ${isPlacing ? 'cursor-crosshair' : ''}`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={() => drag && setDrag(null)}
        data-testid={`plan-image-${planKey}`}
      >
        <img src={src} alt={PLAN_KEYS[planKey].label} className="w-full rounded-lg border border-white/10 select-none pointer-events-none" draggable={false} />

        {/* Existing hotspots */}
        {hotspots.map(u => {
          const h = u.hotspot;
          const isActive = hoveredUnitId === u.id;
          const stylePos = {
            left: `${h.x}%`, top: `${h.y}%`, width: `${h.w}%`, height: `${h.h}%`,
          };
          // When colorByStatus is on, fill the rectangle with the status color
          // (semi-transparent) and use a solid border in the same hue.
          const statusColor = colorByStatus ? STATUS_HOTSPOT[u.status] : null;
          const colorStyle = statusColor ? {
            background: `${statusColor}55`,
            borderColor: statusColor,
            borderWidth: isActive ? '2px' : '1.5px',
            borderStyle: 'solid',
            boxShadow: isActive ? `0 0 0 2px ${statusColor}aa` : 'none',
          } : null;
          return (
            <div
              key={u.id}
              style={{ ...stylePos, ...(colorStyle || {}) }}
              className={`absolute rounded transition-all cursor-pointer ${
                colorStyle ? 'z-10' : (isActive
                  ? 'bg-[var(--accent)]/40 border-2 border-[var(--accent)] shadow-lg z-20'
                  : 'bg-[var(--accent)]/15 border border-[var(--accent)]/60 hover:bg-[var(--accent)]/30 z-10')
              }`}
              onMouseEnter={() => !isPlacing && setHoveredUnitId(u.id)}
              onMouseLeave={() => !isPlacing && setHoveredUnitId(prev => prev === u.id ? null : prev)}
              onClick={(e) => { e.stopPropagation(); if (!isPlacing) onHotspotClick(u); }}
              data-testid={`hotspot-${u.id}`}
              title={`${u.unit_code} · ${STATUS_LABELS[u.status] || u.status}${u.price ? ' · ' + fmtPrice(u.price) : ''}`}
            >
              <span className="absolute -top-5 left-0 text-[10px] font-mono font-bold px-1 rounded whitespace-nowrap"
                style={{
                  background: statusColor || 'var(--accent)',
                  color: '#000',
                }}>
                {u.unit_code}
              </span>
            </div>
          );
        })}

        {/* In-progress drag rectangle */}
        {drag && (
          <div
            className="absolute pointer-events-none rounded bg-[var(--accent)]/30 border-2 border-dashed border-[var(--accent)] z-30"
            style={{ left: `${drag.x}%`, top: `${drag.y}%`, width: `${drag.w}%`, height: `${drag.h}%` }}
          />
        )}
      </div>
      <div className="flex gap-1 mt-2 justify-end">
        <label className="ceo-pill flex items-center gap-1 cursor-pointer" data-testid={`replace-image-${planKey}`}>
          <Upload className="h-3 w-3" /> Replace
          <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
        </label>
        <button onClick={onRemove} className="ceo-pill text-red-400 flex items-center gap-1" data-testid={`remove-image-${planKey}`}>
          <Trash2 className="h-3 w-3" /> Remove
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Projections dashboard — rolls up sale value, market rent, subsidized rent,
// and commercial revenue from the units catalog.
// ----------------------------------------------------------------------

function ProjectionsDashboard({ data }) {
  const cards = [
    {
      label: 'Sale Inventory',
      value: fmtCompact(data.sale.available_value),
      sub: `${data.sale.available_count} unit${data.sale.available_count === 1 ? '' : 's'} for sale`,
      hint: data.sale.sold_count ? `${data.sale.sold_count} sold · ${fmtCompact(data.sale.sold_value)} closed` : null,
      accent: '#22c55e',
      testid: 'proj-sale',
    },
    {
      label: 'Market Rent (Annual)',
      value: fmtCompact(data.market_rent.annual),
      sub: `${data.market_rent.units} unit${data.market_rent.units === 1 ? '' : 's'} · ${fmtCompact(data.market_rent.monthly)}/mo`,
      accent: '#3b82f6',
      testid: 'proj-market-rent',
    },
    {
      label: 'Subsidized Rent (Annual)',
      value: fmtCompact(data.subsidized_rent.annual),
      sub: `${data.subsidized_rent.units} unit${data.subsidized_rent.units === 1 ? '' : 's'} · ${fmtCompact(data.subsidized_rent.monthly)}/mo`,
      accent: '#a855f7',
      testid: 'proj-subsidized',
    },
    {
      label: 'Commercial (Annual)',
      value: fmtCompact(data.commercial.annual),
      sub: `${data.commercial.count} site${data.commercial.count === 1 ? '' : 's'} · ${fmtCompact(data.commercial.monthly)}/mo`,
      accent: '#f97316',
      testid: 'proj-commercial',
    },
  ];

  return (
    <div className="ceo-card-elevated p-5" data-testid="projections-dashboard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>Projections</h3>
          <p className="text-xs opacity-60" style={{ color: 'var(--text)' }}>Rolled up from your unit catalog. Update prices and rents to refine.</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider opacity-60" style={{ color: 'var(--text)' }}>Total Annual Recurring</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }} data-testid="proj-arr">
            {fmtCompact(data.totals.annual_recurring_revenue)}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.testid} className="rounded-lg border border-white/10 p-3" data-testid={c.testid}
            style={{ background: `${c.accent}11` }}>
            <div className="flex items-center gap-1.5 mb-1">
              <div className="h-2 w-2 rounded-full" style={{ background: c.accent }} />
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text)' }}>{c.label}</p>
            </div>
            <p className="text-xl font-bold font-mono" style={{ color: c.accent }}>{c.value}</p>
            <p className="text-[11px] opacity-70 mt-0.5" style={{ color: 'var(--text)' }}>{c.sub}</p>
            {c.hint && <p className="text-[10px] opacity-60 mt-1" style={{ color: 'var(--text)' }}>{c.hint}</p>}
          </div>
        ))}
      </div>
      {/* Status mix bar */}
      {data.totals.all_units > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1.5" style={{ color: 'var(--text)' }}>Status Mix</p>
          <div className="flex h-3 rounded overflow-hidden bg-white/5">
            {Object.entries(data.by_status || {}).map(([status, count]) => {
              const pct = (count / data.totals.all_units) * 100;
              return (
                <div key={status} title={`${STATUS_LABELS[status] || status}: ${count} (${pct.toFixed(0)}%)`}
                  style={{ width: `${pct}%`, background: STATUS_HOTSPOT[status] || '#9ca3af' }} />
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-2 text-[10px]">
            {Object.entries(data.by_status || {}).map(([status, count]) => (
              <span key={status} className="inline-flex items-center gap-1" style={{ color: 'var(--text)' }}>
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_HOTSPOT[status] || '#9ca3af' }} />
                {STATUS_LABELS[status] || status}: <strong>{count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

